// /crm/proposals/:id — detalhe + edição + ações de ciclo da proposta (Story 11.6 + 11.7)
// 11.6: ver a proposta e EDITAR quando status=rascunho (PUT — AC6).
// 11.7: ações de ciclo (enviar/aceitar/recusar/duplicar) + link público + viewedAt (AC5/AC6).

import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { daysDiffLocal, formatBR, formatBRDateTime, todayLocal } from '@control-center/shared';

import { ProposalForm, type ProposalFormValues } from '../../../components/crm/ProposalForm';
import { ProposalActions } from '../../../components/crm/ProposalActions';
import {
  PROPOSAL_STATUS_BADGE,
  PROPOSAL_STATUS_LABELS,
  formatProposalMoney,
  validityFlag,
} from '../../../lib/proposal-helpers';
import { useCrmProposal, useUpdateCrmProposal } from '../../../hooks/use-crm-proposals';
import { useSaasProducts } from '../../../hooks/use-saas-products';
import { usePlans } from '../../../hooks/use-plans';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { extractErrorMessage } from '../../../lib/errors';
import type { CrmProposalDetailEntity } from '../../../lib/crm-proposals-api';

export function CrmProposalDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canWrite } = useCrmPermissions();

  const { data: proposal, isLoading, isError, error } = useCrmProposal(id);
  const { data: saasProducts } = useSaasProducts({ limit: 100 });
  const { data: plans } = usePlans({ limit: 100 });
  const update = useUpdateCrmProposal();

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (isError || !proposal) {
    return (
      <div className="mx-auto max-w-3xl">
        <BackLink onClick={() => navigate('/crm/proposals')} />
        <div className="mt-4 rounded-[14px] border border-bad/40 bg-panel p-6">
          <h2 className="text-[15px] font-semibold text-ink">
            Não conseguimos carregar a proposta.
          </h2>
          <p className="mt-1 text-[12px] text-muted">
            {extractErrorMessage(error, 'Proposta não encontrada ou indisponível.')}
          </p>
        </div>
      </div>
    );
  }

  const isDraft = proposal.status === 'rascunho';
  const editable = canWrite && isDraft;

  const initialValues: Partial<ProposalFormValues> = {
    deal_id: proposal.deal_id,
    lead_id: proposal.lead_id,
    client_id: proposal.client_id,
    saas_product_id: proposal.saas_product_id,
    plan_id: proposal.plan_id,
    billing_cycle: proposal.billing_cycle,
    monthly_value: proposal.monthly_value !== null ? Number(proposal.monthly_value) : null,
    annual_value: proposal.annual_value !== null ? Number(proposal.annual_value) : null,
    setup_value: proposal.setup_value !== null ? Number(proposal.setup_value) : null,
    discount_amount: proposal.discount_amount !== null ? Number(proposal.discount_amount) : null,
    discount_type: proposal.discount_type,
    discount_reason: proposal.discount_reason,
    valid_until: proposal.valid_until,
    observations: proposal.observations,
  };

  const validity = validityFlag(proposal.valid_until);
  const expiringSoon =
    validity === 'soon' && (proposal.status === 'enviada' || proposal.status === 'visualizada');

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 pb-10">
      <header className="flex flex-col gap-2">
        <BackLink onClick={() => navigate('/crm/proposals')} />
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="m-0 text-[26px] font-bold leading-tight tracking-[-0.025em] tabular-nums text-ink">
            {proposal.proposal_number}
          </h1>
          <span
            className={`inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[11px] ${PROPOSAL_STATUS_BADGE[proposal.status]}`}
          >
            {PROPOSAL_STATUS_LABELS[proposal.status]}
          </span>
          {expiringSoon && (
            <span className="inline-flex items-center rounded-[6px] border border-warn/40 px-2 py-[2px] text-[11px] text-warn">
              Vence em {daysUntil(proposal.valid_until)}
            </span>
          )}
        </div>
        {proposal.viewed_at && (
          <p className="text-[12px] text-muted">
            Visualizada em {formatBRDateTime(proposal.viewed_at)}
          </p>
        )}
        <p className="text-[13px] text-muted">
          {editable
            ? 'Proposta em rascunho — você pode editar os campos abaixo e enviá-la quando estiver pronta.'
            : 'Esta proposta não está em rascunho e é somente leitura.'}
        </p>
      </header>

      {/* Ações de ciclo (11.7) — condicionais ao status. */}
      <ProposalActions
        id={proposal.id}
        proposalNumber={proposal.proposal_number}
        status={proposal.status}
        publicToken={proposal.public_token}
        convertedSubscriptionId={proposal.converted_subscription_id}
        canWrite={canWrite}
        leadId={proposal.lead_id}
      />

      {editable ? (
        <ProposalForm
          initialValues={initialValues}
          isPending={update.isPending}
          submitLabel="Salvar alterações"
          saasProducts={saasProducts?.items ?? []}
          plans={plans?.items ?? []}
          linkedDealTitle={proposal.deal?.title ?? null}
          linkedLeadName={proposal.lead?.name ?? null}
          linkedClientName={proposal.client?.name ?? null}
          onCancel={() => navigate('/crm/proposals')}
          onSubmit={async (values) => {
            try {
              await update.mutateAsync({
                id: proposal.id,
                payload: { ...values, version: proposal.version },
              });
              toast.success('Proposta atualizada.', { description: proposal.proposal_number });
            } catch (err) {
              toast.error('Não conseguimos salvar a proposta.', {
                description: extractErrorMessage(err, 'Tente de novo em instantes.'),
              });
            }
          }}
        />
      ) : (
        <ReadOnlyView proposal={proposal} />
      )}
    </div>
  );
}

/** Texto "N dias" até a validade (a partir de hoje, no fuso BR). */
function daysUntil(validUntil: string): string {
  const diff = Math.max(0, daysDiffLocal(todayLocal(), validUntil));
  return diff === 1 ? '1 dia' : `${diff} dias`;
}

function ReadOnlyView({ proposal }: { proposal: CrmProposalDetailEntity }): JSX.Element {
  const rows: { label: string; value: string }[] = [
    { label: 'Produto SaaS', value: proposal.saas_product?.name ?? '—' },
    { label: 'Plano', value: proposal.plan?.name ?? '—' },
    { label: 'Ciclo', value: proposal.billing_cycle },
    { label: 'Mensal', value: formatProposalMoney(proposal.monthly_value) },
    { label: 'Anual', value: formatProposalMoney(proposal.annual_value) },
    { label: 'Setup', value: formatProposalMoney(proposal.setup_value) },
    { label: 'Desconto', value: formatProposalMoney(proposal.discount_amount) },
    { label: 'Validade', value: formatBR(proposal.valid_until) },
  ];

  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 md:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-col gap-[2px]">
            <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-3">
              {r.label}
            </dt>
            <dd className="m-0 text-[13px] text-ink">{r.value}</dd>
          </div>
        ))}
      </dl>
      {proposal.observations && (
        <div className="mt-4 border-t border-line pt-4">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-3">
            Observações
          </p>
          <p className="mt-1 whitespace-pre-wrap text-[13px] text-ink-2">{proposal.observations}</p>
        </div>
      )}
    </section>
  );
}

function BackLink({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex w-fit items-center gap-1 text-[12px] text-muted transition-colors hover:text-ink"
    >
      <ArrowLeft size={13} strokeWidth={2} />
      Propostas
    </button>
  );
}

function DetailSkeleton(): JSX.Element {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      <div className="h-6 w-48 animate-pulse rounded bg-panel-2" />
      <div className="rounded-[14px] border border-line bg-panel p-[20px]">
        <div className="grid grid-cols-2 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded bg-panel-2" />
          ))}
        </div>
      </div>
    </div>
  );
}
